import { useRef } from 'react';
import {
  type KeptExternalParties,
  loadKeptExternalParties,
  saveKeptExternalParties,
} from 'app/utils/helpers/parties-pagination-storage';
import {
  type ExternalPartiesScanResult,
  ApiError,
  QueryEntityKeys,
  QueryKeys,
  ExternalPartiesScanError,
  getExternalPartyChanges,
  scanExternalParties,
} from 'data';
import { useExternalPartiesScanStore } from 'data/stores/external-parties';
import { useQuery } from 'data/utils/useQuery';

const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const isOlderThanADay = (date: string) => Date.now() - new Date(date).getTime() > MAX_AGE_MS;

const scan = async (participantName: string) => {
  const { setScanning } = useExternalPartiesScanStore.getState();
  setScanning(participantName, true);
  try {
    return await scanExternalParties(participantName);
  } finally {
    setScanning(participantName, false);
  }
};

/**
 * Reads the changes since the kept cursor, or scans when nothing is kept, when the last scan or the last use of the
 * cursor is more than a day old (the changes read then gets slow), when the changes can't be read from the cursor
 * (409), or when `forceScan` is set. Keeps the result per participant.
 */
const syncExternalParties = async (participantName: string, forceScan: boolean): Promise<KeptExternalParties> => {
  const kept = loadKeptExternalParties(participantName);
  const needsScan = forceScan || !kept || isOlderThanADay(kept.scannedAt) || isOlderThanADay(kept.cursorUsedAt);

  let result: ExternalPartiesScanResult;
  if (needsScan) {
    result = await scan(participantName);
  } else {
    try {
      result = await getExternalPartyChanges(participantName, kept.cursor);
    } catch (error) {
      if ((error as ApiError)?.status !== 409) throw error;
      result = await scan(participantName);
    }
  }

  const next: KeptExternalParties = {
    parties: result.parties ?? [],
    cursor: result.cursor,
    scannedAt: result.scannedAt,
    cursorUsedAt: new Date().toISOString(),
  };
  saveKeptExternalParties(participantName, next);
  return next;
};

export type ExternalPartiesError = ApiError | ExternalPartiesScanError | Error;

/** External parties of a participant: the kept list straight away, updated on opening, refresh and rescan. */
export function useExternalParties(participantName: string) {
  const forceScan = useRef(false);

  const isScanning = useExternalPartiesScanStore((state) => !!state.scanning[participantName]);

  const { data, isFetching, error, refetch } = useQuery<KeptExternalParties, ExternalPartiesError>(
    [QueryKeys.PARTICIPANTS, participantName, QueryEntityKeys.EXTERNAL_PARTIES_ENTITY],
    () => {
      const force = forceScan.current;
      forceScan.current = false;
      return syncExternalParties(participantName, force);
    },
    {
      enabled: !!participantName,
      // The kept list shows straight away, also when reading the changes or scanning fails.
      initialData: () => loadKeptExternalParties(participantName) ?? undefined,
      initialDataUpdatedAt: () => {
        const kept = loadKeptExternalParties(participantName);
        return kept ? new Date(kept.cursorUsedAt).getTime() : undefined;
      },
    },
  );

  return {
    kept: data,
    isFetching,
    isScanning,
    error: isFetching ? null : error,
    refresh: () => refetch(),
    rescan: () => {
      forceScan.current = true;
      refetch();
    },
  };
}
